import { useMemo, useState } from 'react';
import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow
} from '@carbon/react';
import _ from 'lodash';

import { useNotifications } from 'core/notifications';
import SubGroupModal from 'features/admin/groups/SubGroupModal';
import useMutateUpdateSubGroup from 'shared/api/business-units/useMutateUpdateSubGroup';
import TableToolbarCell from 'shared/components/data-table/TableToolbarCell';
import OverlayLoader from 'shared/components/OverlayLoader';

const SubGroups = ({ businessUnits, onRefreshBusinessUnits, isLoading }) => {
  const { showSuccessNotification, showErrorNotification } = useNotifications();

  const [subGroupEditIndex, setSubGroupEditIndex] = useState(null);

  const updateSubGroupMutation = useMutateUpdateSubGroup({
    onError: () => showErrorNotification({ description: 'Failed to update sub group' }),
    onSuccess: () => {
      setSubGroupEditIndex(null);
      onRefreshBusinessUnits();
      showSuccessNotification({ description: 'Sub group updated successfully' });
    }
  });

  const subGroups = useMemo(
    () => _.sortBy(_.uniqBy(businessUnits?.flatMap((x) => x.subGroups) ?? [], 'id'), 'name'),
    [businessUnits]
  );

  const tableData = subGroups.map((x) => ({
    id: x.id.toString(),
    name: x.name,
    attendanceRate: x.attendanceRate,
    acceptableAttendanceRateDeviation: x.acceptableAttendanceRateDeviation
  }));

  const headers = [
    { key: 'name', header: 'Name' },
    { key: 'attendanceRate', header: 'Attendance Rate' },
    { key: 'acceptableAttendanceRateDeviation', header: 'Acceptable Attendance Rate Deviation' }
  ];

  return (
    <>
      <SubGroupModal
        open={subGroupEditIndex !== null}
        subGroup={subGroupEditIndex === null ? null : subGroups[subGroupEditIndex]}
        isLoading={updateSubGroupMutation.isLoading}
        onClose={() => setSubGroupEditIndex(null)}
        onSubmit={(updatedSubGroup) =>
          updateSubGroupMutation.mutate({
            id: subGroups[subGroupEditIndex].id,
            subGroup: updatedSubGroup
          })
        }
      />
      <DataTable
        headers={headers}
        rows={tableData}
        size="sm"
        render={({ rows, getTableContainerProps, getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer
            title="Sub Groups Administration"
            description="Edit sub groups in the application"
            {...getTableContainerProps()}
          >
            <OverlayLoader small={rows.length === 0} isLoading={isLoading} />
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader {...getHeaderProps({ header })} key={header.key}>
                      {header.header}
                    </TableHeader>
                  ))}
                  <TableToolbarCell.Header />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow {...getRowProps({ row })} key={row.id}>
                    {row.cells.map((cell) => (
                      <TableCell key={cell.id}>{cell.value}</TableCell>
                    ))}
                    <TableToolbarCell>
                      <TableToolbarCell.MenuItem itemText="Edit" onClick={() => setSubGroupEditIndex(index)} />
                    </TableToolbarCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      />
    </>
  );
};

export default SubGroups;
